import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { useIntl } from '@/core/i18n/use-intl';
import {
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui';

interface PaginationNavigationProps {
  currentPage: number;
  totalPages: number;
  pageSize: number;
  totalItems: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  showPageSizeSelector?: boolean;
  pageSizeOptions?: number[];
}

export function PaginationNavigation({
  currentPage,
  totalPages,
  pageSize,
  onPageChange,
  onPageSizeChange,
  showPageSizeSelector = false,
  pageSizeOptions = [10, 20, 30, 40, 50],
}: PaginationNavigationProps) {
  const { t } = useIntl();

  const canGoToPrevious = currentPage > 1;
  const canGoToNext = currentPage < totalPages;

  return (
    <div className="flex items-center justify-between space-x-6 lg:space-x-8">
      <div className="flex w-[100px] ml-2 items-center text-sm font-medium text-muted-foreground">
        {totalPages > 0 ? (
          <>
            {t('pagination.page')} {currentPage} {t('common.of')} {totalPages}
          </>
        ) : (
          <>
            {t('pagination.page')} 0 {t('common.of')} 0
          </>
        )}
      </div>

      <div className="flex items-center space-x-6 lg:space-x-8">
        {showPageSizeSelector && onPageSizeChange && (
          <div className="flex items-center space-x-2">
            <p className="text-sm font-medium text-muted-foreground">
              {t('pagination.rowsPerPage')}
            </p>
            <Select
              value={`${pageSize}`}
              onValueChange={(value) => {
                onPageSizeChange(Number(value));
                // Reset to page 1 when changing page size
                onPageChange(1);
              }}
            >
              <SelectTrigger className="h-8 w-[70px]">
                <SelectValue placeholder={pageSize} />
              </SelectTrigger>
              <SelectContent side="top">
                {pageSizeOptions.map((size) => (
                  <SelectItem key={size} value={`${size}`}>
                    {size}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            className="hidden size-8 p-0 lg:flex"
            onClick={() => onPageChange(1)}
            disabled={!canGoToPrevious}
          >
            <span className="sr-only">{t('pagination.goToFirstPage')}</span>
            <ChevronsLeft className="size-4" />
          </Button>
          <Button
            variant="outline"
            className="size-8 p-0"
            onClick={() => onPageChange(currentPage - 1)}
            disabled={!canGoToPrevious}
          >
            <span className="sr-only">{t('pagination.goToPreviousPage')}</span>
            <ChevronLeft className="size-4" />
          </Button>
          <Button
            variant="outline"
            className="size-8 p-0"
            onClick={() => onPageChange(currentPage + 1)}
            disabled={!canGoToNext}
          >
            <span className="sr-only">{t('pagination.goToNextPage')}</span>
            <ChevronRight className="size-4" />
          </Button>
          <Button
            variant="outline"
            className="hidden size-8 p-0 lg:flex"
            onClick={() => onPageChange(totalPages)}
            disabled={!canGoToNext}
          >
            <span className="sr-only">{t('pagination.goToLastPage')}</span>
            <ChevronsRight className="size-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
